// "Projekti" (every signed-in user, shared by the firm): the projects reminders are sorted into
// when their text mentions the name or an alias, with the aliases (declined forms, how the name is
// spoken), archiving and deleting. Changed online only (a name in use needs an answer at once).
import { useState } from 'react';
import { MAX_PROJECT_ALIASES, MAX_PROJECT_NAME } from '../../../../shared/projects';
import type { Project } from '../../../../shared/types';
import { ApiClientError } from '../../lib/http';
import { addProject, changeProject, removeProject, useProjects } from '../../lib/projects';
import { plural } from '../../lib/format';
import { accountErrorText, FormError } from '../../components/AccountForms';
import { confirmDialog } from '../../components/Dialog';
import { newId } from '../../components/helpers';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/Toasts';
import { LoadError, Loading } from './common';

export const MSG_PROJECTS_OFFLINE = 'Ni povezave s strežnikom, zato projektov zdaj ni mogoče spreminjati. Poskusite znova pozneje.';

/** "Penclja, Penclju" -> ["Penclja", "Penclju"] (one per line or comma). */
export function parseAliases(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((a) => a.trim())
    .filter(Boolean);
}

function offline(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 0;
}

function failureText(err: unknown, fallback: string): string {
  if (offline(err)) return MSG_PROJECTS_OFFLINE;
  if (err instanceof ApiClientError && err.status === 409) return 'Projekt s tem imenom že obstaja.';
  return accountErrorText(err, fallback);
}

interface EditorProps {
  project: Project | null;
  onDone: () => void;
}

/** The form for a new project or for one being edited (name and aliases). */
function ProjectEditor({ project, onDone }: EditorProps) {
  const [name, setName] = useState(project?.name ?? '');
  const [aliases, setAliases] = useState(project?.aliases.join(', ') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const idBase = project ? `project-${project.id}` : 'project-new';

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Vpišite ime projekta.');
      return;
    }
    const list = parseAliases(aliases);
    if (list.length > MAX_PROJECT_ALIASES) {
      setError(`Največ ${MAX_PROJECT_ALIASES} dodatnih oblik imena.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (project) {
        await changeProject(project.id, { name: trimmed, aliases: list });
        toast(`Projekt »${trimmed}« je shranjen.`, { kind: 'success' });
      } else {
        await addProject(newId(), { name: trimmed, aliases: list });
        toast(`Projekt »${trimmed}« je dodan. Opomniki, ki omenjajo to ime, bodo samodejno uvrščeni vanj.`, { kind: 'success' });
      }
      onDone();
    } catch (err) {
      setError(failureText(err, 'Projekta ni bilo mogoče shraniti.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="project-form"
      aria-label={project ? `Uredi projekt ${project.name}` : 'Nov projekt'}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="field">
        <label htmlFor={`${idBase}-name`}>Ime projekta</label>
        <input
          id={`${idBase}-name`}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={MAX_PROJECT_NAME}
          placeholder="npr. BP20"
          autoComplete="off"
          lang="sl"
          data-autofocus
        />
      </div>
      <div className="field">
        <label htmlFor={`${idBase}-aliases`}>Dodatne oblike imena (neobvezno)</label>
        <input
          id={`${idBase}-aliases`}
          type="text"
          value={aliases}
          onChange={(e) => setAliases(e.target.value)}
          placeholder="npr. be pe dvajset, BP 20"
          autoComplete="off"
          lang="sl"
        />
        <p className="muted small">
          Ločene z vejico: kako ime izgovorite (»be pe dvajset«) in sklanjane oblike (»Penclja«, »Penclju«). Prepoznava jih prebere
          enako kot ime.
        </p>
      </div>
      {error && <FormError>{error}</FormError>}
      <div className="form-actions">
        <button type="submit" className="btn btn--primary btn--small" disabled={saving}>
          <Icon name={saving ? 'spinner' : 'check'} size={18} /> {project ? 'Shrani' : 'Dodaj projekt'}
        </button>
        <button type="button" className="btn btn--ghost btn--small" onClick={onDone} disabled={saving}>
          Prekliči
        </button>
      </div>
    </form>
  );
}

function ProjectRow({ project, onEdit }: { project: Project; onEdit: () => void }) {
  const [busy, setBusy] = useState(false);

  const archive = async (archived: boolean) => {
    setBusy(true);
    try {
      await changeProject(project.id, { archived });
      toast(
        archived
          ? `Projekt »${project.name}« je arhiviran: obstoječi dokumenti ostanejo v njem, novi se vanj ne uvrščajo več.`
          : `Projekt »${project.name}« je spet aktiven.`,
        { kind: 'success' },
      );
    } catch (err) {
      toast(failureText(err, 'Spremembe ni bilo mogoče shraniti.'), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await confirmDialog({
      title: `Izbriši projekt »${project.name}«?`,
      message: 'Dokumenti projekta se ne izbrišejo, ostanejo pa brez projekta. Če projekta ne potrebujete več, ga raje arhivirajte.',
      confirmLabel: 'Izbriši',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await removeProject(project.id);
      toast(`Projekt »${project.name}« je izbrisan.`, { kind: 'success' });
    } catch (err) {
      toast(failureText(err, 'Projekta ni bilo mogoče izbrisati.'), { kind: 'error' });
      setBusy(false);
    }
  };

  return (
    <li className={project.archived ? 'is-archived' : undefined} data-testid="project-row">
      <Icon name="folder" size={18} className="muted" />
      <span className="projects-list__name">{project.name}</span>
      {project.archived && <span className="chip chip--neutral">arhiviran</span>}
      <span className="projects-list__actions">
        <button type="button" className="btn btn--ghost btn--small" onClick={onEdit} disabled={busy} aria-label={`Uredi projekt ${project.name}`}>
          <Icon name="pencil" size={18} /> Uredi
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => void archive(!project.archived)}
          disabled={busy}
          aria-label={`${project.archived ? 'Obnovi' : 'Arhiviraj'} projekt ${project.name}`}
        >
          {project.archived ? 'Obnovi' : 'Arhiviraj'}
        </button>
        <button type="button" className="btn btn--danger-ghost btn--small" onClick={() => void remove()} disabled={busy} aria-label={`Izbriši projekt ${project.name}`}>
          <Icon name="trash" size={18} /> Izbriši
        </button>
      </span>
      {project.aliases.length > 0 && <span className="projects-list__aliases">Tudi: {project.aliases.join(', ')}</span>}
    </li>
  );
}

export function ProjectsSection() {
  const projects = useProjects();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const list = projects.data;
  const editable = list !== null && !projects.stale;

  return (
    <section className="card" aria-labelledby="projects-title" id="projekti">
      <h2 id="projects-title" className="card__title">
        Projekti
      </h2>
      <p className="muted">
        Opomnik, ki omenja ime projekta (npr. »BP20, pokliči izvajalca«), se samodejno uvrsti v ta projekt; sestankom in nadzorom projekt
        izberete sami. Imena projektov se samodejno upoštevajo tudi pri prepisu (kot slovar izrazov).
      </p>
      {projects.stale && (
        <div className="notice notice--warning" role="status">
          <Icon name="cloudOff" size={20} className="notice__icon" />
          <div className="notice__content">
            <p>Strežnik ni dosegljiv – projektov zdaj ni mogoče urejati. Prikazan je seznam, shranjen v tej napravi.</p>
            <button type="button" className="btn btn--secondary btn--small" onClick={projects.reload} disabled={projects.loading}>
              <Icon name={projects.loading ? 'spinner' : 'refresh'} size={18} /> Poskusi znova
            </button>
          </div>
        </div>
      )}
      {projects.error && !list ? (
        <LoadError message={projects.error} onRetry={projects.reload} />
      ) : !list ? (
        <Loading />
      ) : (
        <>
          {list.length === 0 ? (
            <p className="muted small" data-testid="projects-empty">
              Še ni projektov.
            </p>
          ) : (
            <ul className="projects-list" aria-label="Projekti">
              {list.map((p) =>
                editingId === p.id ? (
                  <li key={p.id}>
                    <ProjectEditor project={p} onDone={() => setEditingId(null)} />
                  </li>
                ) : (
                  <ProjectRow key={p.id} project={p} onEdit={() => setEditingId(editable ? p.id : null)} />
                ),
              )}
            </ul>
          )}
          {adding ? (
            <ProjectEditor project={null} onDone={() => setAdding(false)} />
          ) : (
            <div className="card__foot">
              <span className="muted small">{plural(list.length, ['projekt', 'projekta', 'projekti', 'projektov'])}</span>
              <button type="button" className="btn btn--primary" onClick={() => setAdding(true)} disabled={!editable}>
                <Icon name="plus" size={20} /> Nov projekt
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

